// API Configuration Management
// This file handles all API-related configuration and environment variables

export const API_CONFIG = {
  // Base URL for API requests (local development default)
  BASE_URL: import.meta.env.VITE_API_BASE_URL ?? 'https://api.emaar.sd',
  
  // Mock data toggle (set to false to use real API)
  USE_MOCK_DATA: import.meta.env.VITE_USE_MOCK_DATA === 'true',
  
  // Authentication endpoints
  AUTH_ENDPOINTS: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
  },
  
  // Admin endpoints
  ADMIN_ENDPOINTS: {
    DASHBOARD: '/admin/dashboard/stats',
    USERS: '/admin/users', 
    BOOKINGS: '/admin/bookings',
    PROPERTIES: '/admin/properties',
    REVIEWS: '/admin/reviews',
    COUPONS: '/admin/coupons',
    ANALYTICS: '/admin/analytics',
    SETTINGS: '/admin/settings',
    SYSTEM_HEALTH: '/admin/system/health',
    ALERTS: '/admin/alerts',
  },
  
  // Request timeout
  TIMEOUT: 30000,
  
  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
} as const;

// Debug mode logging
export const DEBUG = import.meta.env.VITE_DEBUG_MODE === 'true' || import.meta.env.DEV;

// Log API configuration in development
if (DEBUG) {
  console.log('🔧 API Configuration:', {
    baseUrl: API_CONFIG.BASE_URL,
    useMockData: API_CONFIG.USE_MOCK_DATA,
    environment: import.meta.env.MODE,
    debugMode: DEBUG,
  });
}

// Demo credentials for testing
export const DEMO_CREDENTIALS = {
  SUPER_ADMIN: {
    phone: '+966501234567',
    password: 'password',
    role: 'Super Admin'
  },
  ADMIN: {
    phone: '+966501234568', 
    password: 'password',
    role: 'Admin'
  },
  STAFF: {
    phone: '+966501234569',
    password: 'password', 
    role: 'Staff'
  }
} as const;

// Saudi phone number validation
export const SAUDI_PHONE_REGEX = /^\+966[5-9]\d{8}$/;

// Format phone number to Saudi format
export const formatSaudiPhone = (phone: string): string => {
  // Remove all non-digit characters except +
  let digits = phone.replace(/[^\d+]/g, '');
  
  // If it doesn't start with +, add it
  if (!digits.startsWith('+')) {
    digits = '+' + digits.replace(/^\+/, '');
  }
  
  // Handle different input formats
  if (digits.startsWith('+966')) {
    return digits;
  } else if (digits.startsWith('+5') && digits.length <= 12) {
    return '+966' + digits.substring(1);
  } else if (digits.startsWith('+0') && digits.length <= 13) {
    return '+966' + digits.substring(2);
  } else if (digits.startsWith('+') && digits.length > 1) {
    const nums = digits.substring(1);
    if (nums.startsWith('966')) {
      return digits;
    } else if (nums.startsWith('5') && nums.length <= 9) {
      return '+966' + nums;
    } else if (nums.startsWith('0') && nums.length <= 10) {
      return '+966' + nums.substring(1);
    }
  }
  
  return digits;
};

// Validate Saudi phone number
export const isValidSaudiPhone = (phone: string): boolean => {
  return SAUDI_PHONE_REGEX.test(phone);
};

// Check if API is available
export const checkAPIHealth = async (): Promise<boolean> => {
  if (API_CONFIG.USE_MOCK_DATA) {
    return true; // Mock data is always "available"
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${API_CONFIG.BASE_URL}/health`, {
      method: 'GET',
      headers: API_CONFIG.DEFAULT_HEADERS,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.warn('API health check failed:', error);
    return false;
  }
};

export default API_CONFIG;
